import React, { useState, useRef } from 'react';
import { 
  Upload, FileSpreadsheet, AlertCircle, CheckCircle, 
  X, Loader2, ChevronDown, ChevronUp, Users, Calendar,
  MapPin, Clock, AlertTriangle, Link2, HelpCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';

const ImportBookings = ({ token, onImportComplete, onClose }) => {
  const [step, setStep] = useState('upload'); // upload, preview, importing, complete
  const [file, setFile] = useState(null);
  const [includePastEvents, setIncludePastEvents] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    create: true,
    matched: true,
    checking: true,
    update: false,
    skip: false,
  });
  
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.xlsx') && !droppedFile.name.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(droppedFile);
      setError(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handlePreview = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const fileData = await readFileAsBase64(file);
      
      const response = await fetch(`${API_URL}/api/admin/import-bookings/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          file_data: fileData,
          include_past_events: includePastEvents
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to process file');
      }
      
      const data = await response.json();
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!file) return;
    
    setLoading(true);
    setStep('importing');
    setError(null);
    
    try {
      const fileData = await readFileAsBase64(file);
      
      const response = await fetch(`${API_URL}/api/admin/import-bookings/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          file_data: fileData,
          include_past_events: includePastEvents
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to import bookings');
      }
      
      const data = await response.json();
      setResult(data);
      setStep('complete');
      
      // Notify parent to refresh jobs
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      setError(err.message);
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${file ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input"
        />
        
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <FileSpreadsheet className="w-10 h-10 text-green-600" />
            <div className="text-left">
              <p className="font-medium text-green-800">{file.name}</p>
              <p className="text-sm text-green-600">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
              }}
              className="ml-4"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-700">Drop your Excel file here</p>
            <p className="text-sm text-slate-500 mt-1">or click to browse</p>
            <p className="text-xs text-slate-400 mt-2">Supports .xlsx and .xls files</p>
          </>
        )}
      </div>

      {/* Options */}
      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
        <Checkbox
          id="include-past"
          checked={includePastEvents}
          onCheckedChange={setIncludePastEvents}
        />
        <label htmlFor="include-past" className="text-sm text-slate-700 cursor-pointer">
          Include past events (by default, only today and future events are imported)
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button 
          onClick={handlePreview}
          disabled={!file || loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
          data-testid="preview-import-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Preview Import
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    if (!preview) return null;

    const { to_create, to_update, to_matched = [], needs_checking = [], to_skip, unmatched_staff, errors, summary, sheet_name } = preview;

    return (
      <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-testid="import-preview">
        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-2">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-700" data-testid="summary-create-count">{summary.to_create_count}</p>
              <p className="text-xs text-green-600">New Jobs</p>
            </CardContent>
          </Card>
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-indigo-700" data-testid="summary-matched-count">{summary.to_matched_count || 0}</p>
              <p className="text-xs text-indigo-600">Matched Existing</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-700" data-testid="summary-checking-count">{summary.needs_checking_count || 0}</p>
              <p className="text-xs text-amber-600">Needs Checking</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-700" data-testid="summary-update-count">{summary.to_update_count}</p>
              <p className="text-xs text-blue-600">By Order No.</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-slate-600" data-testid="summary-skip-count">{summary.to_skip_count}</p>
              <p className="text-xs text-slate-500">Skipped</p>
            </CardContent>
          </Card>
        </div>

        <p className="text-sm text-slate-500">
          Reading from sheet: <span className="font-medium">{sheet_name}</span>
        </p>

        {/* Unmatched Staff Warning */}
        {unmatched_staff.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Staff names not matched:</p>
              <p className="text-sm text-yellow-700">{unmatched_staff.join(', ')}</p>
              <p className="text-xs text-yellow-600 mt-1">These jobs will be imported but staff won't be assigned automatically.</p>
            </div>
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Processing errors:</p>
              {errors.slice(0, 5).map((err, i) => (
                <p key={i} className="text-sm text-red-700">{err}</p>
              ))}
              {errors.length > 5 && <p className="text-sm text-red-600">...and {errors.length - 5} more</p>}
            </div>
          </div>
        )}

        {/* Matched Existing Jobs */}
        {to_matched.length > 0 && (
          <Card data-testid="matched-section">
            <CardHeader className="cursor-pointer hover:bg-slate-50 py-3" onClick={() => toggleSection('matched')}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-indigo-600" />
                  Matches Existing Job ({to_matched.length})
                </CardTitle>
                {expandedSections.matched ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CardHeader>
            {expandedSections.matched && (
              <CardContent className="pt-0 max-h-80 overflow-y-auto space-y-3">
                <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded p-2">
                  The Order Number will be saved onto these jobs. Only empty fields get filled in — nothing already in the app is overwritten.
                </p>
                {to_matched.map((item, i) => (
                  <div key={i} className="border border-indigo-200 rounded-lg overflow-hidden" data-testid={`matched-item-${i}`}>
                    <div className="flex items-center justify-between bg-indigo-50 px-3 py-2">
                      <div className="text-sm">
                        <p className="font-medium text-indigo-900">{item.client_name} — {item.job_type}</p>
                        <p className="text-xs text-indigo-600">{item.date} · {item.match_reason}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">Order {item.order_number}</Badge>
                    </div>
                    <div className="px-3 py-2">
                      <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 text-xs font-medium text-slate-500 pb-1 border-b">
                        <span>Field</span>
                        <span>In the app</span>
                        <span>Spreadsheet</span>
                      </div>
                      {item.comparison.map((row, j) => (
                        <div key={j} className="grid grid-cols-[1fr_1fr_1fr] gap-2 text-xs py-1 border-b border-slate-50 last:border-0">
                          <span className="text-slate-600">{row.label}</span>
                          <span className={row.action === 'fill' ? 'text-slate-400 italic' : 'text-slate-800'}>
                            {row.app_value === null || row.app_value === undefined ? '—' : String(row.app_value)}
                          </span>
                          <span className={row.action === 'fill' ? 'text-green-700 font-medium' : 'text-slate-400'}>
                            {row.sheet_value === null || row.sheet_value === undefined ? '—' : String(row.sheet_value)}
                            {row.action === 'fill' && <span className="ml-1 text-green-600">(will fill)</span>}
                            {row.action === 'kept_app_value' && <span className="ml-1 text-slate-400">(ignored)</span>}
                          </span>
                        </div>
                      ))}
                      <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 text-xs py-1">
                        <span className="text-slate-600">Staff</span>
                        <span className="text-slate-800">{item.existing_staff?.length ? item.existing_staff.join(', ') : '—'}</span>
                        <span className={item.staff_action === 'added' ? 'text-green-700 font-medium' : 'text-slate-400'}>
                          {item.matched_staff?.length ? item.matched_staff.join(', ') : '—'}
                          {item.staff_action === 'added' && <span className="ml-1 text-green-600">(will add)</span>}
                          {item.staff_action === 'kept_app_staff' && <span className="ml-1 text-slate-400">(app staff kept)</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Needs Checking */}
        {needs_checking.length > 0 && (
          <Card data-testid="needs-checking-section">
            <CardHeader className="cursor-pointer hover:bg-slate-50 py-3" onClick={() => toggleSection('checking')}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-amber-600" />
                  Needs Checking ({needs_checking.length})
                </CardTitle>
                {expandedSections.checking ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CardHeader>
            {expandedSections.checking && (
              <CardContent className="pt-0 max-h-80 overflow-y-auto space-y-3">
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2">
                  Nothing will be created or changed for these rows — decide yourself, then re-import or edit the job by hand.
                </p>
                {needs_checking.map((item, i) => (
                  <div key={i} className="border border-amber-200 rounded-lg overflow-hidden" data-testid={`needs-checking-item-${i}`}>
                    <div className="flex items-center justify-between bg-amber-50 px-3 py-2">
                      <div className="text-sm">
                        <p className="font-medium text-amber-900">{item.client_name} — {item.job_type}</p>
                        <p className="text-xs text-amber-700">Spreadsheet: {item.date} · {item.location || 'No location'}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">Order {item.order_number}</Badge>
                    </div>
                    <div className="px-3 py-2 text-xs">
                      <p className="text-amber-800 mb-2">{item.reason}</p>
                      {item.possible_matches?.map((m, j) => (
                        <div key={j} className="py-1 border-t border-slate-100 text-slate-600">
                          <span className="font-medium text-slate-800">{m.client_name || 'TBC'}</span>
                          {' · '}{m.job_type}{' · '}{m.date}{m.time ? ` · ${m.time}` : ''}
                          {m.location ? ` · ${m.location}` : ''}
                          {m.staff?.length ? ` · Staff: ${m.staff.join(', ')}` : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Jobs to Create */}
        {to_create.length > 0 && (
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 py-3"
              onClick={() => toggleSection('create')}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  New Jobs to Create ({to_create.length})
                </CardTitle>
                {expandedSections.create ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CardHeader>
            {expandedSections.create && (
              <CardContent className="pt-0 max-h-48 overflow-y-auto">
                <div className="space-y-2">
                  {to_create.map((item, i) => (
                    <div key={i} className="p-2 bg-green-50 rounded text-sm border border-green-100">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-green-800">{item.job_type}</p>
                          <p className="text-green-700">{item.client_name}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{item.order_number}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-green-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {item.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {item.time}
                        </span>
                        {item.matched_staff.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" /> {item.matched_staff.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Jobs to Update */}
        {to_update.length > 0 && (
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 py-3"
              onClick={() => toggleSection('update')}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                  Existing Jobs to Update ({to_update.length})
                </CardTitle>
                {expandedSections.update ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CardHeader>
            {expandedSections.update && (
              <CardContent className="pt-0 max-h-48 overflow-y-auto">
                <div className="space-y-2">
                  {to_update.map((item, i) => (
                    <div key={i} className="p-2 bg-blue-50 rounded text-sm border border-blue-100">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-blue-800">{item.job_type}</p>
                          <p className="text-blue-700">{item.client_name}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{item.order_number}</Badge>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        <Calendar className="w-3 h-3 inline mr-1" /> {item.date}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Jobs to Skip */}
        {to_skip.length > 0 && (
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 py-3"
              onClick={() => toggleSection('skip')}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <X className="w-5 h-5 text-slate-500" />
                  Rows to Skip ({to_skip.length})
                </CardTitle>
                {expandedSections.skip ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CardHeader>
            {expandedSections.skip && (
              <CardContent className="pt-0 max-h-48 overflow-y-auto">
                <div className="space-y-1">
                  {to_skip.map((item, i) => (
                    <div key={i} className="p-2 bg-slate-50 rounded text-sm flex justify-between">
                      <span className="text-slate-600">Row {item.row}: {item.order_number || 'No order number'}</span>
                      <span className="text-slate-500">{item.reason}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-white">
          <Button variant="outline" onClick={() => setStep('upload')} className="flex-1">
            Back
          </Button>
          <Button 
            onClick={handleConfirmImport}
            disabled={loading || (to_create.length === 0 && to_update.length === 0 && to_matched.length === 0)}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid="confirm-import-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirm Import
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  const renderImportingStep = () => (
    <div className="py-12 text-center">
      <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-slate-800">Importing Bookings...</h3>
      <p className="text-slate-500 mt-2">Please wait while we process your spreadsheet.</p>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="py-8 text-center space-y-6">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-slate-800">Import Complete!</h3>
        <p className="text-slate-500 mt-1">Your bookings have been imported successfully.</p>
      </div>

      <div className="grid grid-cols-4 gap-3 max-w-xl mx-auto">
        <div className="p-3 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-700">{result?.created || 0}</p>
          <p className="text-xs text-green-600">Created</p>
        </div>
        <div className="p-3 bg-indigo-50 rounded-lg">
          <p className="text-2xl font-bold text-indigo-700">{result?.matched || 0}</p>
          <p className="text-xs text-indigo-600">Matched</p>
        </div>
        <div className="p-3 bg-blue-50 rounded-lg">
          <p className="text-2xl font-bold text-blue-700">{result?.updated || 0}</p>
          <p className="text-xs text-blue-600">Updated</p>
        </div>
        <div className="p-3 bg-amber-50 rounded-lg">
          <p className="text-2xl font-bold text-amber-700">{result?.needs_checking || 0}</p>
          <p className="text-xs text-amber-600">Needs Checking</p>
        </div>
      </div>

      {result?.unmatched_staff?.length > 0 && (
        <div className="text-left p-3 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md mx-auto">
          <p className="text-sm font-medium text-yellow-800">Unmatched staff names:</p>
          <p className="text-sm text-yellow-700">{result.unmatched_staff.join(', ')}</p>
        </div>
      )}

      <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700">
        Done
      </Button>
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            Import Bookings from Spreadsheet
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {step === 'upload' && renderUploadStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'importing' && renderImportingStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportBookings;
